/**
 * node_modules
 */
import React, { createContext, useState, useContext, useEffect, useRef } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { globalHistory } from "@reach/router"

/**
 * context
 */
import { type AreaType } from "@components/AreaProvider"

type Events = Queries.EventsQuery["camp"]["events"]

export type EventsContextProps = {
  events: Events
  setEvents: React.Dispatch<React.SetStateAction<Events>>
  filteredEvents: Events
  setFilteredEvents: React.Dispatch<React.SetStateAction<Events>>
  filter: {
    location?: AreaType | "headquarter"
    date?: {
      start: Date | null
      end: Date | null
    }
    targetAges?: {
      どなたでも: boolean
      小学生以上: boolean
      小学校3年生以上: boolean
      小学校5年生以上: boolean
      中学生以上: boolean
      "20歳以上": boolean
    }
    eventType?: string | null
  }
  setFilter: React.Dispatch<React.SetStateAction<Record<string, unknown>>>
  applyFilter: () => void
}

export type EventsProviderProps = {
  children?: React.ReactNode
  defaultValues: { [K in keyof EventsContextProps]?: EventsContextProps[K] }
}

const EventsContext = createContext<EventsContextProps>({
  events: [],
  setEvents: () => void 0,
  filteredEvents: [],
  setFilteredEvents: () => void 0,
  filter: {
    location: null,
    date: {
      start: null,
      end: null,
    },
    targetAges: {
      どなたでも: false,
      小学生以上: false,
      小学校3年生以上: false,
      小学校5年生以上: false,
      中学生以上: false,
      "20歳以上": false,
    },
    eventType: null,
  },
  setFilter: () => void 0,
  applyFilter: () => void 0,
})

const useEventsContext = () => useContext(EventsContext)

const EventsProvider = ({ children, defaultValues }: EventsProviderProps) => {
  /** query */
  const data: Queries.EventsQuery = useStaticQuery(graphql`
    query Events {
      camp {
        events {
          assemblyAt
          assemblyLocation
          body
          description
          dressCode
          estimatedTime
          eventSchedules {
            startAt
            endAt
          }
          eventType
          images {
            url
            alt
          }
          instructors {
            name
            description
            linkUrl
          }
          location
          maxAttendee
          maxPrice
          minPrice
          note
          pickup
          priceNote
          publicationEndAt
          publishedAt
          registrationEndAt
          registrationNote
          registrationStartAt
          requiredItems
          reservationUrl
          scheduleNote
          status
          targetAge {
            name
          }
          targetAgeNote
          title
          displayLocation
          id
          reservationFlg
        }
      }
    }
  `)

  const dataEvents = data.camp?.events
  const defaultEvents = dataEvents?.filter((event) => !!event)

  // URL からロケーションを抽出（サーバーサイドレンダリング対応）
  const [currentArea, setCurrentArea] = useState<AreaType>(
    typeof window == "undefined" ? null : extractLocation(window.location.pathname),
  )

  // URL変更によるフィルター設定がプログラムによって行われるかを追跡するためのref
  const isUrlChange = useRef(false)
  const isFirstRender = useRef(true)
  // applyFilterを実行するタイミングを制御するためのref
  const shouldApplyFilter = useRef(false)

  // events ステートは初期値のままで良い（全てのイベント）
  const [events, setEvents] = useState<EventsContextProps["events"]>(defaultEvents || [])

  // フィルターのデフォルト値
  const getDefaultFilter = (area: AreaType) => ({
    location: area,
    date: {
      start: null,
      end: null,
    },
    targetAges: {
      どなたでも: false,
      小学生以上: false,
      小学校3年生以上: false,
      小学校5年生以上: false,
      中学生以上: false,
      "20歳以上": false,
    },
    eventType: null,
  })

  // filter の初期値を設定 - currentArea に基づいて更新
  const initialFilter = React.useMemo(() => {
    return {
      ...getDefaultFilter(currentArea ? currentArea : defaultValues.filter?.location || null),
      ...defaultValues.filter,
    }
  }, [defaultValues.filter])

  const [filter, setFilter] = useState<EventsContextProps["filter"]>(initialFilter)

  // filteredEvents の初期値設定
  const [filteredEvents, setFilteredEvents] = useState<EventsContextProps["filteredEvents"]>([])

  // フィルタの適用処理
  const applyFilter = React.useCallback(() => {
    shouldApplyFilter.current = true

    let filtered: Events = events.filter((event) => {
      // キャンプ場のフィルタ適用
      if (!!filter.location && filter.location !== event.location) {
        return false
      }

      // イベントタイプのフィルタ適用
      if (!!filter.eventType && filter.eventType !== event.eventType) {
        return false
      }

      return true
    })

    // 対象年齢のフィルタ適用
    if (filter.targetAges) {
      filtered = filterEventsByAges(filtered, filter.targetAges)
    }

    // 日付のフィルタ適用
    if (filter.date?.start) {
      filtered = filterEventsByDateRange(filtered, filter.date?.start, filter.date?.end)
    }

    setFilteredEvents(filtered)

    // フィルター適用後にフラグをリセット
    shouldApplyFilter.current = false
  }, [events, filter])

  // 自動的にapplyFilterをトリガーしないようにカスタムラップされたsetFilter
  const wrappedSetFilter: React.Dispatch<React.SetStateAction<EventsContextProps["filter"]>> =
    React.useCallback((newFilter) => {
      // applyFilterをトリガーせずにフィルターを更新
      setFilter((prev) => {
        if (typeof newFilter === "function") {
          return newFilter(prev)
        }
        return newFilter
      })
    }, [])

  // Gatsby の環境で URL の変更を監視し、currentArea を更新
  useEffect(() => {
    if (typeof window === "undefined") {
      return // サーバーサイドレンダリング時は何もしない
    }

    return globalHistory.listen(() => {
      const pathname = window.location.pathname
      const newArea = extractLocation(pathname)

      if (newArea !== currentArea) {
        // URL変更によるものであることを示すフラグを設定
        isUrlChange.current = true
        setCurrentArea(newArea)
      }
    })
  }, [currentArea])

  // currentArea が変更された場合、フィルターを完全に初期化し、applyFilter を実行
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    if (currentArea === null) return // エリアが設定されていない場合はスキップ

    if (isUrlChange.current) {
      // このエリアのデフォルトフィルターにリセット
      setFilter(getDefaultFilter(currentArea))
      // この更新後にフィルターを適用すべきことを示す
      shouldApplyFilter.current = true
      // 状態更新後にフィルターを適用
      isUrlChange.current = false
    }
  }, [currentArea])

  // 明示的に要求された場合のみapplyFilterを実行
  useEffect(() => {
    if (events.length > 0 && shouldApplyFilter.current) {
      applyFilter()
    }
  }, [events, filter, applyFilter])

  // コンポーネントマウント時の初期フィルター適用
  useEffect(() => {
    if (events.length > 0) {
      // 初期マウント時にフィルターを適用
      shouldApplyFilter.current = true
      applyFilter()
    }
  }, [])

  const contextValue = {
    events,
    setEvents,
    filteredEvents,
    setFilteredEvents,
    filter,
    setFilter: wrappedSetFilter,
    applyFilter,
  }

  return <EventsContext.Provider value={{ ...contextValue }}>{children}</EventsContext.Provider>
}

/**
 * 指定された日付範囲内に含まれるイベントを抽出する関数
 * @param data イベントデータ
 * @param startDate 検索開始日
 * @param endDate 検索終了日
 * @returns 指定された日付範囲内のイベント
 */
export const filterEventsByDateRange = (
  events: Events,
  startDate: Date,
  endDate: Date | null,
): Events => {
  // 年月日だけを比較するために日付を正規化する関数
  const normalizeDate = (date: Date): Date => {
    const normalized = new Date(date)
    normalized.setHours(0, 0, 0, 0)
    return normalized
  }

  // startDateを正規化
  const normalizedStartDate = normalizeDate(startDate)

  // endDateがnullでない場合は正規化
  const normalizedEndDate = endDate ? normalizeDate(endDate) : null

  // 各イベントをループ処理し、条件に一致するものだけを抽出
  const filteredEvents = events.filter((event) => {
    // いずれかのスケジュールが指定された日付条件に一致するかをチェック
    return event.eventSchedules?.some((schedule) => {
      // スケジュールの開始日を取得して正規化
      const scheduleStart = normalizeDate(new Date(schedule.startAt))

      // endDateがnullの場合、startDateと同じ日付のスケジュールのみを抽出
      if (normalizedEndDate === null) {
        // 日付が一致するかを確認（年月日のみで比較）
        return scheduleStart.getTime() === normalizedStartDate.getTime()
      }

      // スケジュールの終了日を取得して処理
      let scheduleEnd: Date | null = null
      if (schedule.endAt !== null) {
        scheduleEnd = normalizeDate(new Date(schedule.endAt))
      } else {
        // endAtがnullの場合はstartAtと同じ日と見なす
        scheduleEnd = scheduleStart
      }

      // イベントスケジュールが指定範囲と重なるかチェック
      return (
        (scheduleStart >= normalizedStartDate && scheduleStart <= normalizedEndDate) ||
        (scheduleEnd >= normalizedStartDate && scheduleEnd <= normalizedEndDate) ||
        (scheduleStart <= normalizedStartDate && scheduleEnd >= normalizedEndDate)
      )
    })
  })

  return filteredEvents
}

// イベントを年齢条件でフィルタリングする関数
function filterEventsByAges(
  events: Events,
  filterOptions: EventsContextProps["filter"]["targetAges"],
): Events {
  /// フィルターオプションが選択されていない場合、全てのイベントを返す
  if (!filterOptions) return events

  // 選択されたフィルターオプション（チェックされたもの）を配列として取得
  const selectedAgeFilters = Object.entries(filterOptions)
    .filter((isSelectedArray) => isSelectedArray[1])
    .map(([ageName]) => ageName)

  // フィルターが選択されていない場合は全てのイベントを返す
  if (selectedAgeFilters.length === 0) {
    return events
  }

  // フィルタリング処理
  return events.filter((event) => {
    // イベントのtargetAges名前のリストを取得
    const eventTargetAge = event.targetAge?.name

    // selectedAgeFiltersに含まれるフィルターが少なくとも1つ一致するかをチェック
    return selectedAgeFilters.some((selectedAge) => eventTargetAge == selectedAge)
  })
}

// URLからロケーション情報を抽出する関数
export const extractLocation = (url: string): AreaType => {
  // イベント関連ページのみに適用に対応する正規表現
  const patternWithLocation = /^\/([^/]+)\/event(\/.*)?$/

  // URLをパターンとマッチングする
  const match = url.match(patternWithLocation)

  // マッチがあり、最初のキャプチャグループが存在する場合はロケーションとして返す
  if (match && match[1]) {
    // マッチしたパスが separate の場合は headquarter に変更する
    return match[1] == "separate" ? "headquarter" : (match[1] as AreaType)
  }

  // マッチがない、またはURLにロケーションがない場合（単に/eventや/event/page/...の場合）
  return null
}

export { useEventsContext, EventsProvider }
